import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Send, Calendar, Clock, Sparkles } from 'lucide-react';
import { kontaktData, projectData, datenschutzData } from '@/data/mock';
import { toast } from '@/components/ui/sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KontaktView = ({ onNavigate }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    telefon: '',
    slot: '',
    nachricht: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/kontakt`, {
        name: formData.name,
        email: formData.email,
        telefon: formData.telefon,
        slot: formData.slot || '',
        nachricht: formData.nachricht || ''
      });
      toast("Anfrage erfolgreich gesendet", {
        description: "Vielen Dank – wir bestätigen Ihren Platz persönlich per E-Mail.",
      });
      setFormData({ name: '', email: '', telefon: '', slot: '', nachricht: '' });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Bitte versuchen Sie es später erneut oder rufen Sie uns an.";
      toast("Anfrage konnte nicht gesendet werden", { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0d1a0d] via-[#1a2e1a] to-[#0d1a0d] px-5 py-8 md:p-16 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#C9A961]/5 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 md:mb-20">
          
          {/* Decorative Element */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-20 h-px bg-gradient-to-r from-transparent to-[#D4AF37]" />
            <Phone className="w-6 h-6 text-[#D4AF37]" />
            <div className="w-20 h-px bg-gradient-to-l from-transparent to-[#D4AF37]" />
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-7xl font-light text-white mb-4 md:mb-6 tracking-tight" style={{ fontFamily: "'Cormorant', serif" }}>
            {kontaktData.title}
          </h1>
          
          <div className="h-px w-32 mx-auto mb-8 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
          
          <p className="text-lg md:text-xl text-white/70 max-w-4xl mx-auto font-light leading-relaxed">
            {kontaktData.intro}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 mb-12 md:mb-20">
          {/* Besichtigungstermin & Ansprechpartner */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            {/* Besichtigungstermin */}
            <div className="luxury-card luxury-blur rounded-3xl p-6 md:p-10 border border-[#D4AF37]/30">
              <div className="flex items-center gap-3 mb-8">
                <Sparkles className="w-6 h-6 text-[#D4AF37]" />
                <h2 className="text-3xl font-light text-white" style={{ fontFamily: "'Cormorant', serif" }}>
                  Besichtigungstermin
                </h2>
              </div>

              <p className="text-white/75 text-sm md:text-base font-light leading-relaxed mb-8">
                {projectData.besichtigungstermin.beschreibung}
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#C9A961]/20 flex items-center justify-center flex-shrink-0 border border-[#D4AF37]/30">
                    <Calendar className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-[#F4E4C1]/70 text-sm mb-2 uppercase tracking-widest">Datum</p>
                    <p className="text-white text-2xl font-light" style={{ fontFamily: "'Cormorant', serif" }}>
                      {projectData.besichtigungstermin.date}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#C9A961]/20 flex items-center justify-center flex-shrink-0 border border-[#D4AF37]/30">
                    <Clock className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-[#F4E4C1]/70 text-sm mb-2 uppercase tracking-widest">Uhrzeit</p>
                    <p className="text-white text-2xl font-light" style={{ fontFamily: "'Cormorant', serif" }}>
                      {projectData.besichtigungstermin.time}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#C9A961]/20 flex items-center justify-center flex-shrink-0 border border-[#D4AF37]/30">
                    <MapPin className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-[#F4E4C1]/70 text-sm mb-2 uppercase tracking-widest">Ort</p>
                    <p className="text-white text-base font-light leading-relaxed">
                      {projectData.location}
                    </p>
                  </div>
                </div>
              </div>

              {/* Hinweis: begrenzte Gästezahl */}
              <div className="mt-8 pt-6 border-t border-[#D4AF37]/20 flex items-start gap-3">
                <Sparkles className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-1" />
                <p className="text-[#F4E4C1]/80 text-sm font-light leading-relaxed italic">
                  {projectData.besichtigungstermin.hinweisBegrenzt}
                </p>
              </div>
            </div>

            {/* Ansprechpartner */}
            <div className="space-y-4">
              <h3 className="text-2xl font-light text-white mb-6" style={{ fontFamily: "'Cormorant', serif" }}>
                Ihre Ansprechpartner
              </h3>

              {/* Firmen — ProDomo als Hauptpartner oben, Content Immobilien darunter als Kooperationspartner */}
              <div className="space-y-4">
                {kontaktData.firmen.map((firma, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 + idx * 0.1 }}
                    className={`luxury-card luxury-blur rounded-2xl relative overflow-hidden ${
                      firma.istHauptpartner
                        ? 'p-6 md:p-8 border border-[#D4AF37]/50'
                        : 'p-5 md:p-6 border border-[#D4AF37]/20'
                    }`}
                    data-testid={`contact-company-${idx}`}
                  >
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
                    <div className={`flex flex-col sm:flex-row items-center gap-4 sm:gap-6 text-center sm:text-left ${firma.istHauptpartner ? '' : 'opacity-90'}`}>
                      <div className={`flex items-center justify-center flex-shrink-0 rounded-xl ${firma.logoBg === 'light' ? 'bg-white/95' : 'bg-[#0d1a0d]/60 border border-[#D4AF37]/20'} ${firma.istHauptpartner ? 'h-24 md:h-28 w-32 md:w-36 px-3' : 'h-16 md:h-20 w-28 md:w-32 px-3'}`}>
                        <img
                          src={firma.logo}
                          alt={firma.logoAlt}
                          className={firma.istHauptpartner ? firma.logoClasses : 'max-h-full max-w-full object-contain'}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        {firma.istHauptpartner ? (
                          <span className="inline-block text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-1.5">Vertriebspartner</span>
                        ) : (
                          <span className="inline-block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">Kooperationspartnerin</span>
                        )}
                        <h4 className={`font-light text-white mb-1.5 leading-tight ${firma.istHauptpartner ? 'text-xl md:text-2xl' : 'text-base md:text-lg'}`} style={{ fontFamily: "'Cormorant', serif" }}>
                          {firma.name}
                        </h4>
                        <p className={`text-[#F4E4C1]/80 tracking-wide mb-3 ${firma.istHauptpartner ? 'text-sm' : 'text-xs'}`}>
                          {firma.beschreibung}
                        </p>
                        <a
                          href={`mailto:${firma.email}`}
                          className="inline-flex items-center gap-2 text-white/80 hover:text-[#D4AF37] transition-colors"
                        >
                          <Mail className="w-4 h-4 flex-shrink-0" />
                          <span className={`font-light break-all ${firma.istHauptpartner ? 'text-sm' : 'text-xs'}`}>{firma.email}</span>
                        </a>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {kontaktData.ansprechpartner.map((person, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                  className="luxury-card luxury-blur rounded-2xl p-6 md:p-8 border border-[#D4AF37]/20 hover:border-[#D4AF37]/40 transition-all duration-500 group ml-0 md:ml-8"
                  data-testid={`contact-person-${index}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h4 className="text-xl font-light text-white" style={{ fontFamily: "'Cormorant', serif" }}>
                      {person.name}
                    </h4>
                    {person.firma && (
                      <span className="text-[10px] uppercase tracking-widest text-[#D4AF37]/90 border border-[#D4AF37]/40 rounded-full px-2 py-0.5 whitespace-nowrap flex-shrink-0">
                        {person.firma}
                      </span>
                    )}
                  </div>
                  <p className="text-[#F4E4C1] text-sm mb-4 tracking-wide">{person.rolle}</p>
                  <a
                    href={`tel:${person.telefon}`}
                    className="inline-flex items-center gap-3 text-white/80 hover:text-[#D4AF37] transition-colors group"
                  >
                    <Phone className="w-4 h-4" />
                    <span className="font-light">{person.telefon}</span>
                  </a>
                  {person.email && (
                    <a
                      href={`mailto:${person.email}`}
                      className="mt-2 flex items-center gap-3 text-white/80 hover:text-[#D4AF37] transition-colors group"
                    >
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      <span className="font-light break-all">{person.email}</span>
                    </a>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <form onSubmit={handleSubmit} className="luxury-card luxury-blur rounded-3xl p-6 md:p-10 border border-[#D4AF37]/30 space-y-6">
              <h2 className="text-3xl font-light text-white mb-8" style={{ fontFamily: "'Cormorant', serif" }}>
                Kontaktformular
              </h2>
              
              <div>
                <label htmlFor="name" className="block text-[#F4E4C1] text-sm mb-3 font-light tracking-wide">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-5 py-4 bg-white/5 border border-[#D4AF37]/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] transition-all duration-300"
                  placeholder="Ihr Name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-[#F4E4C1] text-sm mb-3 font-light tracking-wide">
                  E-Mail *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-5 py-4 bg-white/5 border border-[#D4AF37]/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] transition-all duration-300"
                  placeholder="ihre.email@beispiel.de"
                />
              </div>

              <div>
                <label htmlFor="telefon" className="block text-[#F4E4C1] text-sm mb-3 font-light tracking-wide">
                  Telefon *
                </label>
                <input
                  type="tel"
                  id="telefon"
                  name="telefon"
                  value={formData.telefon}
                  onChange={handleChange}
                  required
                  className="w-full px-5 py-4 bg-white/5 border border-[#D4AF37]/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] transition-all duration-300"
                  placeholder="+49 ..."
                />
              </div>

              <div>
                <label htmlFor="slot" className="block text-[#F4E4C1] text-sm mb-3 font-light tracking-wide">
                  Gewünschtes Zeitfenster am {projectData.besichtigungstermin.date}
                </label>
                <select
                  id="slot"
                  name="slot"
                  value={formData.slot}
                  onChange={handleChange}
                  data-testid="slot-select"
                  className="w-full px-5 py-4 bg-white/5 border border-[#D4AF37]/20 rounded-xl text-white focus:outline-none focus:border-[#D4AF37] transition-all duration-300"
                >
                  <option value="" className="bg-[#1a2e1a]">Bitte wählen</option>
                  {projectData.besichtigungstermin.slots.map((slot, i) => (
                    <option key={i} value={slot} className="bg-[#1a2e1a]">{slot}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="nachricht" className="block text-[#F4E4C1] text-sm mb-3 font-light tracking-wide">
                  Ihre Nachricht
                </label>
                <textarea
                  id="nachricht"
                  name="nachricht"
                  value={formData.nachricht}
                  onChange={handleChange}
                  rows="5"
                  className="w-full px-5 py-4 bg-white/5 border border-[#D4AF37]/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] transition-all duration-300 resize-none"
                  placeholder="Ihre Nachricht an uns..."
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-anfrage-btn"
                className="luxury-button w-full px-6 py-5 text-white rounded-xl transition-all duration-500 flex items-center justify-center gap-3 shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="font-light tracking-wider uppercase text-sm">
                  {submitting ? 'Wird gesendet...' : 'Anfrage absenden'}
                </span>
                {!submitting && <Send className="w-5 h-5" />}
              </button>

              {/* Bestätigungshinweis */}
              <p className="text-sm text-[#F4E4C1]/80 font-light leading-relaxed text-center">
                Nach dem Absenden prüfen wir Ihre Anfrage und bestätigen Ihnen Ihren Platz persönlich per E-Mail.
              </p>

              {/* A3 · Pflichthinweis unter dem Formular */}
              <p data-testid="form-privacy-hint" className="text-xs text-white/50 font-light leading-relaxed text-center">
                Mit dem Absenden werden Ihre Angaben zur Bearbeitung Ihrer Anfrage und zur Terminvereinbarung verarbeitet. Einzelheiten:{' '}
                <button
                  type="button"
                  data-testid="form-privacy-link"
                  onClick={() => onNavigate && onNavigate('datenschutz')}
                  className="text-[#D4AF37] hover:underline"
                >
                  Datenschutzerklärung
                </button>
                .
              </p>
            </form>
          </motion.div>
        </div>

        {/* Image Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="rounded-3xl overflow-hidden h-[240px] md:h-[400px] relative group"
        >
          <img
            src={projectData.images.entrance}
            alt="Green Living – Leben am Wasser, Eingangs-Ansicht · unverbindliche Visualisierung, mit KI bearbeitet (AI MODIFIED)"
            className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
          />
          <span className="absolute top-4 right-4 z-20 text-[10px] uppercase tracking-widest text-white/70 bg-black/40 backdrop-blur-sm px-3 py-1 rounded-full border border-white/10">
            Visualisierung
          </span>
          <img
            src="/badges/ai_modified_white.png"
            alt="Mit KI bearbeitet (AI MODIFIED)"
            className="absolute bottom-4 left-4 z-20 h-5 md:h-6 w-auto opacity-95 drop-shadow-md"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d1a0d] via-transparent to-transparent" />
          
          {/* Gold Corners */}
          <div className="absolute top-0 left-0 w-32 h-32">
            <div className="absolute top-0 left-0 w-20 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
            <div className="absolute top-0 left-0 h-20 w-px bg-gradient-to-b from-[#D4AF37] to-transparent" />
          </div>
          
          <div className="absolute bottom-10 left-0 right-0 text-center px-8">
            <p className="text-white/95 text-2xl md:text-4xl font-light italic" style={{ fontFamily: "'Cormorant', serif" }}>
              „Ein Ort zum Ankommen.“
            </p>
          </div>
        </motion.div>
      </div>
      
      {/* Grain Texture */}
      <div className="grain-texture absolute inset-0 pointer-events-none" />
    </div>
  );
};

export default KontaktView;
